/**
 * @fileoverview 陸上管理者からのネットワーク音声通話（VoIP）着信を受ける常駐コンポーネント
 * @description 着信(invite)を検知して全画面の着信UIを表示し、応答するとWebRTCで音声通話する。
 *   トランスポートは2系統:
 *   - ネイティブ(APK): 常駐フォアグラウンドサービス(SignalingService)がWSを保持し、Voiceプラグイン
 *     経由でシグナリングを橋渡し。アプリがバックグラウンド/画面オフでも着信でき、オンライン維持もする。
 *   - ブラウザ: 直接WebSocketで接続（フォアグラウンド時のみ）。
 *   WebRTC(getUserMedia/RTCPeerConnection)はどちらもWebView側で処理する。
 */
import React, { useEffect, useRef, useState } from "react";
import { registerPlugin } from "@capacitor/core";
import { COMPANY_ID } from "../services/api";

const API_BASE = (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ?? "/api/v1";
const SIGNAL_URL = API_BASE.replace(/^http/, "ws") + "/voice/signal";

interface VoiceApi {
  configure(o: { token: string; companyId: string; apiBase: string }): Promise<void>;
  send(o: { data: string }): Promise<void>;
  endCall(): Promise<void>;
  addListener(event: "signal", cb: (ev: { data: string }) => void): Promise<{ remove: () => void }>;
}
const Voice = registerPlugin<VoiceApi>("Voice");

// ネイティブ側から evaluateJavascript で読める診断ブレッドクラム（logcatに出す用）
const dbg = (patch: Record<string, unknown>) => {
  try {
    const w = window as unknown as { __voiceDbg?: Record<string, unknown> };
    w.__voiceDbg = { ...(w.__voiceDbg ?? {}), ...patch, t: new Date().toISOString().slice(11, 19) };
  } catch { /* ignore */ }
};

type CallState = "idle" | "ringing" | "connecting" | "in_call";
interface IceServer { urls: string | string[]; username?: string; credential?: string; }

export const CallReceiver: React.FC = () => {
  const [state, setState] = useState<CallState>("idle");
  const [muted, setMuted] = useState(false);
  const [callerName, setCallerName] = useState<string>("");

  const wsRef = useRef<WebSocket | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const iceServersRef = useRef<IceServer[]>([{ urls: "stun:stun.l.google.com:19302" }]);
  const pendingOfferRef = useRef<RTCSessionDescriptionInit | null>(null);
  const pendingRemoteIceRef = useRef<RTCIceCandidateInit[]>([]);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const ringCtxRef = useRef<{ ctx: AudioContext; stop: () => void } | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef<CallState>("idle");
  stateRef.current = state;
  // 実際に使っているトランスポート。native経路が失敗したらwsへ切替える。
  const transportRef = useRef<"native" | "ws">("ws");

  // ── シグナリング送出（ネイティブ=プラグイン / ブラウザ=WS） ──
  const sendSig = (obj: unknown) => {
    const data = JSON.stringify(obj);
    if (transportRef.current === "native") { Voice.send({ data }).catch(() => {}); return; }
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(data);
  };

  // ── 呼出音（WebAudio）＋バイブ ─────────────────────────────
  const startRing = () => {
    try { navigator.vibrate?.([600, 400, 600, 400, 600, 400]); } catch { /* ignore */ }
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctx();
      let stopped = false;
      const beep = () => {
        if (stopped) return;
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine"; o.frequency.value = 880;
        g.gain.value = 0.0001;
        o.connect(g); g.connect(ctx.destination);
        const t = ctx.currentTime;
        g.gain.exponentialRampToValueAtTime(0.25, t + 0.05);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.6);
        o.start(t); o.stop(t + 0.65);
      };
      beep();
      const iv = setInterval(beep, 1500);
      ringCtxRef.current = { ctx, stop: () => { stopped = true; clearInterval(iv); try { ctx.close(); } catch { /* ignore */ } } };
    } catch { /* audio不可でもバイブ/表示で気づける */ }
  };
  const stopRing = () => {
    try { navigator.vibrate?.(0); } catch { /* ignore */ }
    ringCtxRef.current?.stop();
    ringCtxRef.current = null;
  };

  // ── 通話後片付け ─────────────────────────────────────────
  const teardown = (notifyPeer: boolean) => {
    stopRing();
    if (notifyPeer) sendSig({ type: "bye" });
    if (transportRef.current === "native") Voice.endCall().catch(() => {});
    try { pcRef.current?.close(); } catch { /* ignore */ }
    pcRef.current = null;
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    localStreamRef.current = null;
    if (audioElRef.current) audioElRef.current.srcObject = null;
    pendingOfferRef.current = null;
    pendingRemoteIceRef.current = [];
    setMuted(false);
    setCallerName("");
    setState("idle");
  };

  // ── WebRTC 応答側 ────────────────────────────────────────
  const buildPc = (): RTCPeerConnection => {
    const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
    pc.onicecandidate = e => { if (e.candidate) sendSig({ type: "ice", candidate: e.candidate }); };
    pc.ontrack = e => {
      if (audioElRef.current && e.streams[0]) {
        audioElRef.current.srcObject = e.streams[0];
        audioElRef.current.play().catch(() => { /* autoplay制限。応答操作後なので通常は再生可 */ });
      }
    };
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === "connected") setState("in_call");
      else if (s === "failed" || s === "disconnected" || s === "closed") {
        if (stateRef.current !== "idle") teardown(false);
      }
    };
    return pc;
  };

  const accept = async () => {
    stopRing();
    if (transportRef.current === "native") Voice.endCall().catch(() => {});
    const offer = pendingOfferRef.current;
    if (!offer) { teardown(true); return; }
    setState("connecting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      const pc = buildPc();
      pcRef.current = pc;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      for (const c of pendingRemoteIceRef.current) { try { await pc.addIceCandidate(c); } catch { /* ignore */ } }
      pendingRemoteIceRef.current = [];
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      sendSig({ type: "answer", sdp: answer });
      sendSig({ type: "accept" });
    } catch {
      sendSig({ type: "reject", reason: "media_error" });
      teardown(false);
      alert("マイクを利用できませんでした。設定でマイクの権限を許可してください。");
    }
  };

  const reject = () => { sendSig({ type: "reject", reason: "declined" }); teardown(false); };
  const hangup = () => teardown(true);

  const toggleMute = () => {
    const s = localStreamRef.current;
    if (!s) return;
    const next = !muted;
    s.getAudioTracks().forEach(t => { t.enabled = !next; });
    setMuted(next);
  };

  // ── シグナリング受信の共通処理 ───────────────────────────
  const handleSignal = async (text: string) => {
    let msg: { type: string; [k: string]: unknown };
    try { msg = JSON.parse(text); } catch { return; }
    switch (msg.type) {
      case "ice_config":
        if (Array.isArray(msg["iceServers"])) iceServersRef.current = msg["iceServers"] as IceServer[];
        break;
      case "invite":
        if (stateRef.current !== "idle") { dbg({ invite: "busy" }); sendSig({ type: "reject", reason: "busy" }); break; }
        pendingOfferRef.current = (msg["sdp"] as RTCSessionDescriptionInit) ?? null;
        pendingRemoteIceRef.current = [];
        setCallerName(typeof msg["caller"] === "string" && msg["caller"] ? String(msg["caller"]) : "");
        dbg({ invite: "ringing" });
        setState("ringing");
        startRing();
        break;
      case "ice": {
        const cand = msg["candidate"] as RTCIceCandidateInit;
        const pc = pcRef.current;
        if (pc && pc.remoteDescription) { try { await pc.addIceCandidate(cand); } catch { /* ignore */ } }
        else pendingRemoteIceRef.current.push(cand);
        break;
      }
      case "cancel":
      case "bye":
        if (stateRef.current !== "idle") teardown(false);
        break;
    }
  };

  // ── 接続（ネイティブ=プラグイン購読 / ブラウザ=WS） ────────
  useEffect(() => {
    let closedByUs = false;
    let removeListener: (() => void) | null = null;
    let cfgTimer: ReturnType<typeof setInterval> | null = null;
    let settled = false; // native確定 or ws開始済み

    let wsToken: string | null = null;      // 現在のWS接続に使ったトークン
    let cfgToken: string | null = null;     // configure済みトークン

    // 直接WebSocket（ブラウザ/旧APK・前面時のみ有効）
    const connectWs = () => {
      if (settled) return;
      settled = true;
      transportRef.current = "ws";
      console.log("[voice] transport=ws (fallback)");
      const connect = () => {
        const token = localStorage.getItem("catms_device_token");
        if (!token) { reconnectRef.current = setTimeout(connect, 10_000); return; }
        wsToken = token;
        const url = `${SIGNAL_URL}?role=device&company_id=${encodeURIComponent(COMPANY_ID)}&token=${encodeURIComponent(token)}`;
        let ws: WebSocket;
        try { ws = new WebSocket(url); } catch { reconnectRef.current = setTimeout(connect, 5_000); return; }
        wsRef.current = ws;
        ws.onmessage = ev => { handleSignal(typeof ev.data === "string" ? ev.data : ""); };
        ws.onclose = () => { wsRef.current = null; if (!closedByUs) reconnectRef.current = setTimeout(connect, 3_000); };
        ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
      };
      connect();
    };

    // 常駐サービス(Voiceプラグイン)を必ず試す。isNativePlatformに頼らず、実際に
    // configureが通れば native、失敗（ブラウザ/旧APKでプラグイン未実装）したらWSへ。
    dbg({ mounted: true, listener: "adding" });
    Voice.addListener("signal", ev => { dbg({ lastSignal: String((() => { try { return (JSON.parse(ev.data) as { type?: string }).type; } catch { return "parse_err"; } })()) }); handleSignal(ev.data); })
      .then(h => { removeListener = h.remove; dbg({ listener: "ready" }); })
      .catch(e => { dbg({ listener: "failed:" + String(e) }); });

    const tryConfigure = () => {
      const token = localStorage.getItem("catms_device_token");
      if (!token) { dbg({ cfg: "no_token" }); return; } // トークン登録待ち（登録後にconfigure）
      if (settled && transportRef.current === "ws") {
        // トークンが変わった（再発行）ら、WSを張り替えて新トークンで再接続
        if (wsToken && wsToken !== token) { try { wsRef.current?.close(); } catch { /* ignore */ } }
        return;
      }
      if (settled && transportRef.current === "native" && cfgToken === token) return;
      cfgToken = token;
      Voice.configure({ token, companyId: COMPANY_ID, apiBase: API_BASE })
        .then(() => { settled = true; transportRef.current = "native"; dbg({ cfg: "native_ok" }); console.log("[voice] transport=native (service configured)"); })
        .catch(e => { dbg({ cfg: "rejected:" + String(e) }); connectWs(); }); // プラグイン未実装 → WS
    };
    tryConfigure();
    cfgTimer = setInterval(tryConfigure, 8_000);

    return () => {
      closedByUs = true;
      if (cfgTimer) clearInterval(cfgTimer);
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      removeListener?.();
      try { wsRef.current?.close(); } catch { /* ignore */ }
      teardown(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── UI ───────────────────────────────────────────────────
  const overlay: React.CSSProperties = {
    position: "fixed", inset: 0, zIndex: 100000,
    background: "rgba(4,20,40,0.96)", color: "#fff",
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    gap: 24, textAlign: "center", padding: 24,
  };
  const btn = (bg: string): React.CSSProperties => ({
    width: 84, height: 84, borderRadius: "50%", border: "none", color: "#fff",
    background: bg, fontSize: 30, cursor: "pointer", boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
  });

  return (
    <>
      <audio ref={audioElRef} autoPlay playsInline />

      {state === "ringing" && (
        <div style={overlay}>
          <div style={{ fontSize: 18, opacity: 0.85 }}>📞 着信</div>
          <div style={{ fontSize: 34, fontWeight: 700 }}>{callerName || "陸上管理者"}</div>
          <div style={{ fontSize: 15, opacity: 0.75 }}>{callerName ? "（陸上管理者）" : ""}</div>
          <div style={{ fontSize: 14, opacity: 0.7 }}>応答するとマイクを使用します</div>
          <div style={{ display: "flex", gap: 56, marginTop: 8 }}>
            <div>
              <button style={btn("#e53935")} onClick={reject} aria-label="拒否">✕</button>
              <div style={{ marginTop: 8, fontSize: 13 }}>拒否</div>
            </div>
            <div>
              <button style={btn("#43a047")} onClick={accept} aria-label="応答">✓</button>
              <div style={{ marginTop: 8, fontSize: 13 }}>応答</div>
            </div>
          </div>
        </div>
      )}

      {(state === "connecting" || state === "in_call") && (
        <div style={overlay}>
          <div style={{ fontSize: 18, opacity: 0.85 }}>{callerName || "陸上管理者"}と通話中</div>
          <div style={{ fontSize: 44 }}>🎧</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>
            {state === "connecting" ? "接続中…" : "通話中"}
          </div>
          <div style={{ display: "flex", gap: 40, marginTop: 8 }}>
            <div>
              <button style={btn(muted ? "#9e9e9e" : "#455a64")} onClick={toggleMute} aria-label="ミュート">
                {muted ? "🔇" : "🎙"}
              </button>
              <div style={{ marginTop: 8, fontSize: 13 }}>{muted ? "ミュート中" : "ミュート"}</div>
            </div>
            <div>
              <button style={btn("#e53935")} onClick={hangup} aria-label="終了">📵</button>
              <div style={{ marginTop: 8, fontSize: 13 }}>終了</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CallReceiver;
